import { useMediaQuery } from '@mui/material';
import { format } from 'date-fns';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { SecondaryTextTypography } from '../../../App.styles';
import articlePlaceholder from '../../../assets/articlePlaceholder.png';
import { useThemeContext } from '../../../global-state/themeContext';
import { StockCountItem } from '../../../hooks';
import { Typography } from '../../../stories/atoms';
import { ColumnFlexContainer, RowFlexContainer } from '../../../stories/atoms/containerV2/ContainerV2';
import { Animation, Animations, BannerItemsType } from '../../../stories/molecules';
import { HeaderRowItem, Table } from '../../../stories/molecules/tablev2/Table';
import { muiTheme } from '../../../theme';
import { homeTranslations, soldProductsTranslations, yearlyStockTakeTranslations } from '../../../translations';
import { getArticleAssetUrl } from '../../../utils/soldProducts';
import { formatSeason, getSeason } from '../util';
import { CheckedItem } from '../YearlyStockTakePage';
import { StyledItem } from './StockTable.styles';

const NoDataAnimation = () => {
  const { t } = useTranslation();
  const {
    stockTable: { noData: noDataTranslations },
  } = yearlyStockTakeTranslations;
  return (
    <ColumnFlexContainer justifyContent="center" padding={[12, 0, 0]}>
      <Animation
        size="small"
        title={t(noDataTranslations.title.key, noDataTranslations.title.defaultValue)}
        subtitle={t(noDataTranslations.subtitle.key, noDataTranslations.subtitle.defaultValue)}
        animation={Animations.SAD}
      />
    </ColumnFlexContainer>
  );
};

enum StockColumnType {
  Photo = 'photo',
  ArticleNumber = 'articleNumber',
  Season = 'season',
  Product = 'product',
  Department = 'department',
  LastUpdated = 'lastUpdated',
  Index = 'index',
  SubIndex = 'subindex',
  Action = 'action',
}

type StockTableProps = {
  stockCount: StockCountItem[];
  bannerItems: BannerItemsType[];
  noData?: boolean;
  checkedItems: CheckedItem[];
  handleCheckedItems: (sicScope: string, isChecked: boolean) => void;
  handleModalDetails: (articleId: string) => void;
  handleViewDetails: (articleId: StockCountItem) => void;
};

export const StockTable: FC<StockTableProps> = ({
  stockCount,
  bannerItems = [],
  noData = false,
  // checkedItems,
  // handleCheckedItems,
  // handleModalDetails,
  handleViewDetails,
}) => {
  const { t } = useTranslation();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('tabletPortrait'));
  const { mode } = useThemeContext();
  const { stockTable: stockTableTranslations } = yearlyStockTakeTranslations;
  const { common: commonTranslations } = homeTranslations;

  const headerItems: HeaderRowItem<StockColumnType>[] = [
    {
      id: StockColumnType.Photo,
      content: (
        <Typography>{t(stockTableTranslations.photo.key, stockTableTranslations.photo.defaultValue)}</Typography>
      ),
    },
    {
      id: StockColumnType.ArticleNumber,
      content: (
        <Typography>
          {t(stockTableTranslations.articleNumber.key, stockTableTranslations.articleNumber.defaultValue)}
        </Typography>
      ),
    },
    {
      id: StockColumnType.Season,
      content: (
        <Typography>
          {t(soldProductsTranslations.season.key, soldProductsTranslations.season.defaultValue)}
        </Typography>
      ),
    },
    {
      id: StockColumnType.Product,
      content: (
        <Typography>
          {t(stockTableTranslations.product.key, stockTableTranslations.product.defaultValue)}
        </Typography>
      ),
    },
    {
      id: StockColumnType.Department,
      content: (
        <Typography>
          {t(stockTableTranslations.departmentName.key, stockTableTranslations.departmentName.defaultValue)}
        </Typography>
      ),
    },
    {
      id: StockColumnType.LastUpdated,
      content: (
        <Typography>
          {t(commonTranslations.lastUpdated.key, commonTranslations.lastUpdated.defaultValue)}
        </Typography>
      ),
    },
    {
      id: StockColumnType.Index,
      content: (
        <Typography>{t(stockTableTranslations.index.key, stockTableTranslations.index.defaultValue)}</Typography>
      ),
    },
    {
      id: StockColumnType.SubIndex,
      content: (
        <Typography>
          {t(stockTableTranslations.subindex.key, stockTableTranslations.subindex.defaultValue)}
        </Typography>
      ),
    },
    // {
    //   id: StockColumnType.Action,
    //   content: (
    //     <Typography>{t(stockTableTranslations.action.key, stockTableTranslations.action.defaultValue)}</Typography>
    //   ),
    // },  Hiding this for card: BTCOBUI-4350
  ];

  // const getIsChecked = (sicScope: string) => {
  //   const checkedItem = checkedItems.find((item) => item.sicScope === sicScope);
  //   if (!checkedItem) return false;
  //   return checkedItem.isChecked;
  // };

  return (
    <>
      {!isMobile && (
        <ColumnFlexContainer
          data-testid="stock-inventory-table"
          flex={1}
          padding={[2, 0]}
          minHeight={[0]}
          maxHeight={bannerItems.length !== 0 ? 'calc(-290px + 100vh)' : 'calc(-260px + 100vh)'}
        >
          <Table
            items={stockCount}
            headerItems={headerItems}
            cellSize="small"
            getRowItems={(stockItem: StockCountItem) => {
              const imageUrl = getArticleAssetUrl({
                articleId: `${stockItem.articleId}`,
                assetType: 'descriptiveStillLife',
                rendition: 'medium',
              });
              return [
                {
                  id: StockColumnType.Photo,
                  width: '8%',
                  content: (
                    <RowFlexContainer>
                      {/* <Checkbox  Hiding this for card: BTCOBUI-4350
                        onChange={(checked) => {
                          handleCheckedItems(stockItem.sicScope, checked);
                        }}
                        label=""
                        isDisabled={stockItem.status === 'NotFound'}
                        isChecked={getIsChecked(stockItem.sicScope)}
                      /> */}
                      <StyledItem
                        data-testid="article-image"
                        src={imageUrl}
                        alt={stockItem.productName}
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.src = articlePlaceholder;
                        }}
                        width="100%"
                        height="100%"
                      />
                    </RowFlexContainer>
                  ),
                },
                { id: StockColumnType.ArticleNumber, content: <Typography>{stockItem.sicScope}</Typography> },
                {
                  id: StockColumnType.Season,
                  content: <Typography>{getSeason(stockItem.sicScope)}</Typography>,
                },
                {
                  id: StockColumnType.Product,
                  content: (
                    <ColumnFlexContainer>
                      <Typography>{stockItem.productId}</Typography>
                      <Typography color="textGray" type="caption">
                        {stockItem.productName}
                      </Typography>
                    </ColumnFlexContainer>
                  ),
                },
                {
                  id: StockColumnType.Department,
                  content: (
                    <ColumnFlexContainer>
                      <Typography>{stockItem.departmentNumber}</Typography>
                      <Typography color="textGray" type="caption">
                        {stockItem.departmentName}
                      </Typography>
                    </ColumnFlexContainer>
                  ),
                },
                {
                  id: StockColumnType.LastUpdated,
                  content: (
                    <Typography>
                      {stockItem.lastUpdatedTimestamp
                        ? format(new Date(stockItem.lastUpdatedTimestamp), "yyyy-MM-dd 'at' HH:mm")
                        : '-'}
                    </Typography>
                  ),
                },
                { id: StockColumnType.Index, content: <Typography>{stockItem.index}</Typography> },
                { id: StockColumnType.SubIndex, content: <Typography>{stockItem.subIndex}</Typography> },
                // {
                //   id: StockColumnType.Action,   Hiding this for card: BTCOBUI-4350
                //   content: (
                //     <StyledIconWithText
                //       data-testid="action-button"
                //       label={
                //         stockItem.status === 'NotFound'
                //           ? t(foundItemTranslations.button.key, foundItemTranslations.button.defaultValue)
                //           : t(notFoundTranslations.button.key, notFoundTranslations.button.defaultValue)
                //       }
                //       statusType={stockItem.status}
                //       onClick={() => {
                //         if (stockItem.status !== 'Picked') handleModalDetails(stockItem.sicScope);
                //       }}
                //       textOptions={{
                //         type: 'button',
                //       }}
                //       padding={[1, 2]}
                //       containerStyles={{ borderRadius: 8 }}
                //     />
                //   ),
                // },
              ];
            }}
            stickyHeader
          />
          {noData && <NoDataAnimation />}
        </ColumnFlexContainer>
      )}

      {isMobile && (
        <ColumnFlexContainer
          data-testid="stock-inventory-grid"
          overflow="auto"
          gap={[3]}
          maxHeight={bannerItems.length !== 0 ? 'calc(-350px + 100vh)' : 'calc(-310px + 100vh)'}
        >
          {stockCount.map((stockItem) => {
            const imageUrl = getArticleAssetUrl({
              articleId: `${stockItem.articleId}`,
              assetType: 'descriptiveStillLife',
              rendition: 'medium',
            });

            return (
              <RowFlexContainer
                data-testid="stock-inventory-grid-item"
                key={stockItem.sicScope}
                position="relative"
                hasBorder
                borderRadius={[2]}
                justifyContent="between"
                backgroundColor={mode === 'dark' ? 'backgroundPaperDark' : 'white'}
              >
                <RowFlexContainer alignItems="start" width="100%">
                  {/* <Checkbox Hiding this for card: BTCOBUI-4350
                    onChange={(checked) => {
                      handleCheckedItems(stockItem.sicScope, checked);
                    }}
                    label=""
                    isDisabled={stockItem.status === 'NotFound'}
                    isChecked={getIsChecked(stockItem.sicScope)}
                  /> */}
                  <RowFlexContainer
                    maxWidth={[18]}
                    minWidth={[18]}
                    height={'100%'}
                    backgroundColor={mode === 'dark' ? 'backgroundTableHeaderDark' : 'baseGray'}
                    justifyContent="center"
                    alignItems="center"
                    onClick={() => handleViewDetails(stockItem)}
                    borderRadius={[2, 0, 0, 2]}
                  >
                    <StyledItem
                      data-testid="article-image"
                      src={imageUrl}
                      alt={stockItem.productName}
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.src = articlePlaceholder;
                      }}
                    />
                  </RowFlexContainer>
                  <ColumnFlexContainer padding={[2, 1, 2, 2]} width="100%">
                    <RowFlexContainer justifyContent="between" margin={[0, 0, 1, 0]}>
                      <Typography type="body4">{stockItem.productName}</Typography>
                      <ColumnFlexContainer
                        padding={[1, 2]}
                        alignSelf="end"
                        backgroundColor="validationGreen"
                        borderRadius={[1]}
                        margin={[0, 0, 0, 1]}
                      >
                        <Typography color="white" type="body3">
                          {formatSeason(stockItem.sicScope)}
                        </Typography>
                      </ColumnFlexContainer>
                    </RowFlexContainer>

                    <ColumnFlexContainer gap={[1]} position="relative" width="100%">
                      <RowFlexContainer flexWrap="wrap">
                        <SecondaryTextTypography type="caption">
                          {t(
                            stockTableTranslations.articleNumber.key,
                            stockTableTranslations.articleNumber.defaultValue,
                          )}
                          :
                        </SecondaryTextTypography>
                        <Typography type="body4"> {stockItem.sicScope}</Typography>
                      </RowFlexContainer>
                      <RowFlexContainer>
                        <SecondaryTextTypography type="caption">
                          {t(stockTableTranslations.productId.key, stockTableTranslations.productId.defaultValue)}:
                        </SecondaryTextTypography>
                        <Typography type="caption"> {stockItem.productId}</Typography>
                      </RowFlexContainer>
                      <RowFlexContainer flexWrap="wrap">
                        <SecondaryTextTypography type="caption">
                          {t(
                            stockTableTranslations.department.key,
                            stockTableTranslations.department.defaultValue,
                          )}
                          :
                        </SecondaryTextTypography>
                        <Typography type="caption">
                          {' '}
                          {`${stockItem.departmentName} (${stockItem.departmentNumber})`}
                        </Typography>
                      </RowFlexContainer>
                    </ColumnFlexContainer>
                  </ColumnFlexContainer>
                </RowFlexContainer>

                {/* Hiding this for card: BTCOBUI-4350 */}
                {/* <RowFlexContainer position="absolute" top="12px" right="12px">
                  <StyledIconWithText
                    data-testid="action-button"
                    label={
                      stockItem.status === 'NotFound'
                        ? t(foundItemTranslations.button.key, foundItemTranslations.button.defaultValue)
                        : t(notFoundTranslations.button.key, notFoundTranslations.button.defaultValue)
                    }
                    statusType={stockItem.status}
                    onClick={() => {
                      if (stockItem.status !== 'Picked') handleModalDetails(stockItem.sicScope);
                    }}
                    textOptions={{
                      type: 'body2',
                    }}
                    padding={[1, 2]}
                    containerStyles={{ borderRadius: 6 }}
                  />
                </RowFlexContainer> */}
              </RowFlexContainer>
            );
          })}
          {noData && <NoDataAnimation />}
        </ColumnFlexContainer>
      )}
    </>
  );
};
